import {
  migrateLegacyGlobalCooldownCards,
  skillEffectiveCooldownMs,
} from "@/character/skills/cooldown-upgrade";
import { SKILL_SLOT_MAX } from "@/character/skills/constants";
import { buildSkillRuntimePayload } from "@/character/skills/runtime";
import { loadSkillBar, saveSkillBar, skillById } from "@/character/skills/storage";
import type { SkillBarState } from "@/character/skills/types";
import { applySkillChainGcdToOtherSlots } from "@/character/skills/skill-chain-gcd";
import { bumpSlotCooldown, updateSkillBarCooldownDom } from "@/character/skills/skill-bar-cooldown";
import { HomeEvents } from "@/home/home-events";
import {
  bindPlaySkillBar,
  emitPlaySkillBarMasterAutoCombat,
  renderPlaySkillBarSlotsHtml,
  syncPlaySkillBarMasterAutoUi,
} from "@/home/home-play-skill-bar";
import { createDefaultHomeEconomy, type HomeEconomy } from "@/home/economy";
import { loadHomeEconomyFromIdb } from "@/home/home-idb";
import type { HomeSkillRuntimePayload } from "@/home/home-events";
import {
  TowerEvents,
  type TowerSkillUseAcceptedPayload,
  type TowerSkillUseRejectedPayload,
} from "@/tower/tower-events";

export type TowerSkillBarHandle = {
  getRuntime: () => HomeSkillRuntimePayload;
  getSkillBar: () => SkillBarState;
  refresh: () => void;
  tryAuto: () => void;
  syncMasterAutoCombat: () => void;
  destroy: () => void;
};

export async function mountTowerSkillBar(
  root: HTMLElement,
  userId: string,
  isBattleActive: () => boolean,
): Promise<TowerSkillBarHandle> {
  let economy: HomeEconomy =
    (await loadHomeEconomyFromIdb(userId)) ?? createDefaultHomeEconomy();
  let skillBar: SkillBarState = await loadSkillBar(userId);
  const legacy = migrateLegacyGlobalCooldownCards(skillBar, economy);
  skillBar = legacy.bar;
  economy = legacy.economy;

  const skillCooldownUntilBySlot: number[] = Array.from({ length: SKILL_SLOT_MAX }, () => 0);
  const skillCooldownDurationBySlot: number[] = Array.from({ length: SKILL_SLOT_MAX }, () => 0);
  const skillSlotPending: boolean[] = Array.from({ length: SKILL_SLOT_MAX }, () => false);
  let cdTimer: ReturnType<typeof setInterval> | null = null;

  const skillCooldownRemainingMs = (slotIndex: number): number => {
    const until = skillCooldownUntilBySlot[slotIndex] ?? 0;
    return Math.max(0, until - Date.now());
  };

  const dispatchRuntime = () => {
    window.dispatchEvent(
      new CustomEvent(HomeEvents.SkillRuntimeChanged, {
        detail: buildSkillRuntimePayload(skillBar, economy),
      }),
    );
  };

  const syncCooldownProgress = () => {
    const bar = root.querySelector<HTMLElement>("#tower-skill-bar");
    if (!bar) return;
    updateSkillBarCooldownDom(
      bar,
      skillCooldownRemainingMs,
      (index) => skillCooldownDurationBySlot[index] ?? 0,
      { respectDisabled: true },
    );
  };

  const applySkillSlotCooldown = (slotIndex: number): void => {
    const skillId = skillBar.slots[slotIndex];
    const used = skillId ? skillById(skillBar, skillId) : undefined;
    const cd = used ? skillEffectiveCooldownMs(used) : 30_000;
    bumpSlotCooldown(
      skillCooldownUntilBySlot,
      skillCooldownDurationBySlot,
      slotIndex,
      Date.now() + cd,
      cd,
    );
  };

  const useSkillSlot = (slotIndex: number, skillId: string): void => {
    if (!isBattleActive()) return;
    if (skillSlotPending[slotIndex]) return;
    if (skillCooldownRemainingMs(slotIndex) > 0) return;
    skillSlotPending[slotIndex] = true;
    window.dispatchEvent(
      new CustomEvent(HomeEvents.UseSkill, {
        detail: { skillId, slotIndex },
      }),
    );
  };

  const onSkillUseRejected = (event: Event): void => {
    const { slotIndex } = (event as CustomEvent<TowerSkillUseRejectedPayload>).detail;
    if (!Number.isFinite(slotIndex) || slotIndex < 0 || slotIndex >= SKILL_SLOT_MAX) return;
    skillSlotPending[slotIndex] = false;
  };

  const onSkillUseAccepted = (event: Event): void => {
    const { slotIndex, chainGcdMs } = (event as CustomEvent<TowerSkillUseAcceptedPayload>).detail;
    if (!Number.isFinite(slotIndex) || slotIndex < 0 || slotIndex >= SKILL_SLOT_MAX) return;
    skillSlotPending[slotIndex] = false;
    applySkillSlotCooldown(slotIndex);
    if (Number.isFinite(chainGcdMs) && chainGcdMs > 0) {
      applySkillChainGcdToOtherSlots(
        skillCooldownUntilBySlot,
        skillCooldownDurationBySlot,
        slotIndex,
        chainGcdMs,
      );
    }
    syncHud();
  };

  const tryAuto = (): void => {
    if (!isBattleActive()) return;
    if (!skillBar.masterAuto) return;
    for (let index = 0; index < SKILL_SLOT_MAX; index += 1) {
      if (!skillBar.slotAuto[index]) continue;
      const skillId = skillBar.slots[index];
      if (!skillId) continue;
      if (skillCooldownRemainingMs(index) > 0) continue;
      useSkillSlot(index, skillId);
    }
  };

  const syncHud = () => {
    const bar = root.querySelector<HTMLElement>("#tower-skill-bar");
    if (!bar) return;
    const disabled = !isBattleActive();
    bar.innerHTML = renderPlaySkillBarSlotsHtml({
      skillBar,
      unlocked: economy.skillSlotsUnlocked,
      skillCooldownRemainingMs,
      disabled,
      offClass: disabled ? "tower-skill-slot--off" : undefined,
    });
    syncPlaySkillBarMasterAutoUi(root, skillBar, economy.skillSlotsUnlocked);
    syncCooldownProgress();
  };

  const unbindPlaySkillBar = bindPlaySkillBar(root, {
    barSelector: "#tower-skill-bar",
    getSkillBar: () => skillBar,
    getUnlocked: () => economy.skillSlotsUnlocked,
    onUseSkill: (slotIndex, skillId) => {
      useSkillSlot(slotIndex, skillId);
      tryAuto();
    },
    onMasterAutoChange: (enabled) => {
      emitPlaySkillBarMasterAutoCombat(enabled);
    },
    onSkillBarUpdated: () => {
      void saveSkillBar(userId, skillBar);
      syncHud();
      dispatchRuntime();
    },
  });

  dispatchRuntime();
  syncHud();
  cdTimer = setInterval(() => {
    if (skillCooldownUntilBySlot.some((until) => until > Date.now())) {
      syncCooldownProgress();
    }
    tryAuto();
  }, 100);

  const onEconomyChanged = (event: Event) => {
    const detail = (event as CustomEvent<{ economy: HomeEconomy }>).detail;
    if (!detail?.economy) return;
    economy = detail.economy;
    syncHud();
  };
  window.addEventListener(HomeEvents.EconomyChanged, onEconomyChanged);
  window.addEventListener(TowerEvents.SkillUseRejected, onSkillUseRejected);
  window.addEventListener(TowerEvents.SkillUseAccepted, onSkillUseAccepted);

  const syncMasterAutoCombat = () => {
    if (skillBar.masterAuto === true) {
      emitPlaySkillBarMasterAutoCombat(true);
    }
    tryAuto();
  };

  return {
    getRuntime: () => buildSkillRuntimePayload(skillBar, economy),
    getSkillBar: () => skillBar,
    refresh: () => {
      syncHud();
      dispatchRuntime();
    },
    tryAuto,
    syncMasterAutoCombat,
    destroy: () => {
      unbindPlaySkillBar();
      if (cdTimer) clearInterval(cdTimer);
      window.removeEventListener(HomeEvents.EconomyChanged, onEconomyChanged);
      window.removeEventListener(TowerEvents.SkillUseRejected, onSkillUseRejected);
      window.removeEventListener(TowerEvents.SkillUseAccepted, onSkillUseAccepted);
    },
  };
}
